<template>
  <footer id="evy-footer" class="site-footer">
    <div class="container site-footer__inner">
      <div class="site-footer__accent" aria-hidden="true">
        <span class="site-footer__accent-line site-footer__accent-line--left"></span>
        <span class="site-footer__accent-mark"></span>
        <span class="site-footer__accent-line site-footer__accent-line--right"></span>
      </div>

      <div class="site-footer__brand">
        <p class="site-footer__brand-name">Haus Birkengrün</p>
        <p class="site-footer__copy">
          &copy; {{ currentYear }} · Alle Rechte vorbehalten
        </p>
      </div>

      <nav class="site-footer__legal" aria-label="Rechtliches">
        <div class="site-footer__link-group site-footer__link-group--primary">
          <button type="button" class="site-footer__link" @click="openPopup('imprint', 'Impressum')">
            Impressum
          </button>
          <button
            type="button"
            class="site-footer__link"
            @click="openPopup('data_protection', 'Datenschutzerklärung')"
          >
            Datenschutz
          </button>
          <button type="button" class="site-footer__link" @click="openPopup('terms', 'AGB')">
            AGB
          </button>
        </div>

        <div class="site-footer__link-group site-footer__link-group--secondary">
          <button type="button" class="site-footer__link" @click="openCookieSettings">
            Cookie-Einstellungen
          </button>
          <a
            href="https://myportal.de"
            target="_blank"
            rel="noopener noreferrer"
            class="site-footer__link site-footer__link--muted"
          >
            Admin
          </a>
        </div>
      </nav>
    </div>
  </footer>
</template>

<script>
import { useContent } from '../composables/useContent.js'
import { useCookieConsent } from '../composables/useCookieConsent.js'

export default {
  setup() {
    const content = useContent()
    const { openCookieSettings } = useCookieConsent()
    const currentYear = new Date().getFullYear()

    return {
      content,
      openCookieSettings,
      currentYear
    }
  },
  methods: {
    async openPopup(identifier, title) {
      await this.content.loadContent({ force: true })

      const contentItem = this.content.getContentByIdentifier(identifier)
      if (contentItem?.content) {
        const { content_popup } = this.$parent
        content_popup.is_open = true
        content_popup.content = contentItem.content
        content_popup.title = contentItem.title || title
      }
    }
  }
}
</script>

<style scoped lang="scss">
.site-footer {
  padding: 2.5rem 0 2.75rem;
  background: linear-gradient(180deg, #2f383c 0%, #283033 100%);
  text-align: center;
}

.site-footer__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.site-footer__accent {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 1.5rem;
}

.site-footer__accent-line {
  display: block;
  width: clamp(2.5rem, 10vw, 4rem);
  height: 1px;
  flex-shrink: 0;

  &--left {
    background: linear-gradient(90deg, transparent 0%, rgba(66, 217, 228, 0.45) 100%);
  }

  &--right {
    background: linear-gradient(90deg, rgba(66, 217, 228, 0.45) 0%, transparent 100%);
  }
}

.site-footer__accent-mark {
  display: block;
  width: 0.3125rem;
  height: 0.3125rem;
  border-radius: 50%;
  background: #42d9e4;
  box-shadow: 0 0 0 4px rgba(66, 217, 228, 0.12);
}

.site-footer__brand {
  margin-bottom: 1.35rem;
}

.site-footer__brand-name {
  margin: 0 0 0.35rem;
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.82);
}

.site-footer__copy {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.42);
}

.site-footer__legal {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.85rem;
  width: 100%;
  max-width: 22rem;
}

.site-footer__link-group {
  display: flex;
  align-items: center;
  justify-content: center;
}

.site-footer__link-group--primary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

.site-footer__link-group--secondary {
  flex-direction: column;
  gap: 0.65rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.site-footer__link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.68);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: #42d9e4;
  }

  &--muted {
    color: rgba(255, 255, 255, 0.36);
  }
}

.site-footer__link-group--primary .site-footer__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0.5rem 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.01em;

  &:hover,
  &:focus-visible {
    background: rgba(66, 217, 228, 0.08);
    border-color: rgba(66, 217, 228, 0.28);
  }
}

@media (min-width: 768px) {
  .site-footer {
    padding: 2.25rem 0 2.5rem;
  }

  .site-footer__brand {
    margin-bottom: 1.1rem;
  }

  .site-footer__brand-name {
    font-size: 1rem;
  }

  .site-footer__legal {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.35rem 0.75rem;
    max-width: none;
  }

  .site-footer__link-group--primary {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0;
  }

  .site-footer__link-group--primary .site-footer__link {
    min-height: auto;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: none;
    font-size: 0.8125rem;
    font-weight: inherit;

    &:hover,
    &:focus-visible {
      background: none;
      border-color: transparent;
    }

    &:not(:last-child)::after {
      content: '';
      display: inline-block;
      width: 3px;
      height: 3px;
      margin-left: 0.75rem;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.22);
      vertical-align: middle;
    }
  }

  .site-footer__link-group--secondary {
    flex-direction: row;
    gap: 0.75rem;
    padding-top: 0;
    border-top: 0;

    &::before {
      content: '';
      display: block;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.22);
      flex-shrink: 0;
    }
  }

  .site-footer__link-group--secondary .site-footer__link:not(:last-child)::after {
    content: '';
    display: inline-block;
    width: 3px;
    height: 3px;
    margin-left: 0.75rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.22);
    vertical-align: middle;
  }
}

@media (max-width: 767px) {
  .site-footer {
    padding: 1.75rem 0 max(1.75rem, env(safe-area-inset-bottom));
  }

  .site-footer__accent {
    margin-bottom: 1.25rem;
  }

  .site-footer__brand {
    margin-bottom: 1.15rem;
    padding: 0 0.5rem;
  }

  .site-footer__brand-name {
    font-size: 0.875rem;
  }

  .site-footer__copy {
    font-size: 0.75rem;
  }

  .site-footer__legal {
    max-width: min(100%, 20rem);
    padding: 0 0.25rem;
  }

  .site-footer__link-group--secondary {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem 1.25rem;
    padding-top: 0.75rem;
  }

  .site-footer__link-group--secondary .site-footer__link {
    font-size: 0.75rem;
  }
}
</style>
